import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime.js";
import utc from "dayjs/plugin/utc.js";
import * as R from "remeda";
import * as ISODateString from "./iso-date-string.js";
import * as MonthCode from "./month-code.js";
import { defaultMonthsRangeSettings, relativeRowToRowId, relativeRowToRowTypeLookup } from "./rows.js";
import * as ShortMonth from "./short-month.js";
import * as ShortMonthYY from "./short-month-yy.js";
import * as TwoDigitYear from "./two-digit-year.js";
import { londonNow } from "./time.js";
dayjs.extend(utc);
dayjs.extend(relativeTime);
function getMonthRowIds(numMonths) {
    const currentMonth = dayjs(londonNow()).startOf("month");
    const currentMonthIndex = currentMonth.month();
    const currentYear = currentMonth.year() % 100;
    return Array.from({ length: numMonths }, (_, i) => {
        const offsetMonthIndex = ((currentMonthIndex + i) % 12);
        const offsetYear = (currentYear + Math.floor((currentMonthIndex + i) / 12));
        return MonthCode.fromMonthCodeAndYear(MonthCode.monthCodeFromMonthIndex(offsetMonthIndex), TwoDigitYear.fromTwoDigitYearNumber(offsetYear));
    });
}
export const monthRowIds = getMonthRowIds(defaultMonthsRangeSettings);
export const currentMonth = dayjs(londonNow()).startOf("month");
export const miscPeriodToCode = (period) => period.replace(" ", "");
/**
 * Determine the grid row type from an absolute period string.
 * Accepts both spaced and compact forms (e.g. "Q1 26" and "Q126").
 * Month names are matched via the real month list so "Cal26"/"Sum26"
 * are not misclassified as months. Anything unrecognised (e.g. adhoc
 * spread formulas like "FEB26/MAR26") falls back to "adhoc".
 */
export function rowTypeFromPeriod(period) {
    if (ShortMonthYY.isCiShortMonthYY(period))
        return "mth";
    if (/^Q[1-4] ?\d{2}$/i.test(period))
        return "qtr";
    if (/^H[12] ?\d{2}$/i.test(period))
        return "hlv";
    if (/^Cal ?\d{2}$/i.test(period))
        return "cal";
    if (/^(Sum|Win) ?\d{2}$/i.test(period))
        return "season";
    return "adhoc";
}
/**
 * stricter and faster equivalent of `rowStringToCode`
 * for a stricter set of inputs that includes the other period strings
 */
export function strictRowStringToCode(rowString) {
    if (ShortMonthYY.isShortMonthYY(rowString)) {
        return MonthCode.fromShortMonthYY(rowString);
    }
    // fallback - for anything that isn't a ShortMonthYY, e.g. H2 28, Q3 30, Win 25, Sum 26, Cal 27 etc.
    // can be even surprising non-periods like "-" which just so happens to result in "--"
    // TODO: if this is always a `MiscPeriod`, this is effectively equivalent to `miscPeriodToCode` and could be replaced
    const first3 = rowString.substring(0, 3);
    const yearPart = rowString.slice(-2);
    return `${first3}${yearPart}`.replace(" ", "");
}
// TODO: find callsites that can move to strictRowStringToCode
export function rowStringToCode(rowStr) {
    if (rowStr) {
        return strictRowStringToCode(rowStr);
    }
    console.warn("rowStr is undefined in rowStringToCode function");
    return "";
}
/**
 * The reason this isn't in ISODateString.ts is because it assumes:
 * 1. the year must be 20XX
 * 2. the day must be the 1st
 * These are feature-specific assumptions and not strictly relevant to ISODateString
 */
export function dateFromMonthCode(code) {
    // Extract the year assuming it's in the 2000s
    const year = TwoDigitYear.fromMonthCodeYY(code);
    const month = ShortMonth.monthNumberStringFromMonthCodeYY(code);
    // Construct and return the month string in the format YYYY-MM-DD
    return `20${year}-${month}-01`;
}
/**
 * Converts a month code like 'F23' to a month string in the format YYYY-MM-DD.
 *
 * @param code - The month code.
 * @returns - The month string in the format YYYY-MM-DD.
 * @throws - If the code is invalid.
 */
export function monthCodeToMonthString(code) {
    // Validate the code format
    if (!MonthCode.is(code)) {
        throw new Error(`Code must be valid MonthCodeYY: ${code}`);
    }
    return dateFromMonthCode(code);
}
function currentMonthToCode(currentMonth) {
    return strictRowStringToCode(ShortMonthYY.fromDayjs(currentMonth));
}
// this can throw, so we're assuming it is valid
const currentMonthCode = MonthCode.schema.parse(currentMonthToCode(currentMonth));
const emptyMonths = Array.from({ length: defaultMonthsRangeSettings + 1 }, (_, i) => i);
const monthCodeToOffsetIndex = Object.fromEntries(emptyMonths.map((i) => {
    const monthCode = genMonthCodeWithOffset(currentMonthCode, i);
    return [monthCode, i];
}));
const offsetToMonthCodeIndex = R.invert(monthCodeToOffsetIndex);
/**
 * The return type is unsafe unless you're sure the provided offset is within bounds
 * @param offset This must be within bounds of the default months range, currently 0 - 59
 */
export function monthCodeFromOffset(offset) {
    return offsetToMonthCodeIndex[offset];
}
const shortMonthToOffsetIndex = Object.fromEntries(emptyMonths.map((i) => {
    const monthCode = genMonthCodeWithOffset(currentMonthCode, i);
    const shortMonth = ShortMonthYY.fromMonthCodeYY(monthCode);
    return [shortMonth, i];
}));
const longMonthToOffsetIndex = Object.fromEntries(emptyMonths.map((i) => {
    const monthCode = genMonthCodeWithOffset(currentMonthCode, i);
    const longMonth = monthCodeToMonthString(monthCode);
    return [longMonth, i];
}));
const rowToOffset = {
    ...monthCodeToOffsetIndex,
    ...shortMonthToOffsetIndex,
    ...longMonthToOffsetIndex,
}; // a sop to improve the TS inference - need to keep these in sync
const tryRowToOffset = (key) => rowToOffset[key];
export const offsetToRow = R.invert(longMonthToOffsetIndex);
export function relativeRowToRowType(rowId) {
    const type = relativeRowToRowTypeLookup[rowId];
    if (type !== undefined) {
        return type;
    }
    throw new Error("Could not find row type for rowId");
}
/**
 * @deprecated use `nthNextMonthsByYearAndMonth` instead
 */
export function nthNextMonths(monthRows) {
    return monthRows.map((m, idx) => [idx + 1, ShortMonthYY.fromDayjs(m)]);
}
export const nthNextMonthsByYearAndMonth = (monthRows) => monthRows.map(({ year, month }, idx) => [
    idx + 1,
    ShortMonthYY.fromShortMonthAndYear(ShortMonth.fromMonthIndex(month), year),
]);
/**
 * @deprecated use `genYearAndMonthList` instead
 */
export function genMonthList(n, date) {
    return Array.from({ length: n }, (_, offset) => {
        return date.add(offset, "month");
    });
}
export const addMonth = (deltaMonths, { year, month }) => {
    const newMonths = month + deltaMonths;
    // Calculate year change. Each step of 12 months equals one year.
    // This calculation correctly handles negative offsets.
    const yearsChange = Math.floor(newMonths / 12);
    return {
        year: TwoDigitYear.add(yearsChange, year),
        month: MonthCode.monthIndexFromNumber(newMonths),
    };
};
export const yearAndMonthFromDate = (date) => ({
    year: TwoDigitYear.fromDate(date),
    month: MonthCode.monthIndexFromDate(date),
});
/**
 * This is much, much faster than `genMonthList`
 * dayjs has some extremely inefficient code that `genMonthList` uses
 * e.g. https://github.com/iamkun/dayjs/blob/dev/src/index.js#L194
 * dynamically computing method names from strings, then
 * determining the days in each month, despite that the day is never used
 * in the results of `genMonthList`
 */
export function genYearAndMonthList(n, date) {
    const yearAndMonth = yearAndMonthFromDate(date);
    return Array.from({ length: n }, (_, offset) => addMonth(offset, yearAndMonth));
}
/**
 * @deprecated use `yearAndMonthToFormatted` instead
 */
function dateToFormattedBy(date, period, numMonths) {
    if (!date)
        throw new Error("`date` is undefined in dateToFormattedBy function");
    const monthInt = date.month() + 1;
    const year = date.format("YY");
    return `${period}${Math.floor((monthInt - 1) / numMonths) + 1} ${year}`;
}
const yearAndMonthToFormatted = (period, numMonths) => (year, month) => `${period}${Math.floor(month / numMonths) + 1} ${year}`;
/**
 * @deprecated use `nthNextPeriodsByYearAndMonth` instead
 */
function nthNextPeriods(dates, showCurrent, formatter) {
    // the Set dedupes as multiple identical dates may produce the same string, formatter is usually not injective
    const periods = Array.from(new Set(dates.map(formatter))).map((q, idx) => [idx + 1, q]);
    return showCurrent ? periods : periods.slice(1);
}
const nthNextPeriodsByYearAndMonth = (formatter) => (dates, showCurrent) => {
    // the Set dedupes as multiple identical dates may produce the same string, formatter is usually not injective
    const periods = Array.from(new Set(dates.map(({ year, month }) => formatter(year, month)))).map((q, idx) => [idx + 1, q]);
    return showCurrent ? periods : periods.slice(1);
};
function dateToFormattedQtr(date) {
    return dateToFormattedBy(date, "Q", 3);
}
/**
 * @deprecated use `nthNextQtrsByYearAndMonth` instead
 */
export function nthNextQtrs(dates, showCurrent) {
    return nthNextPeriods(dates, showCurrent, dateToFormattedQtr);
}
export const nthNextQtrsByYearAndMonth = nthNextPeriodsByYearAndMonth(yearAndMonthToFormatted("Q", 3));
function dateToFormattedHlv(date) {
    return dateToFormattedBy(date, "H", 6);
}
/**
 * @deprecated use `nthNextHlvsByYearAndMonth` instead
 */
export function nthNextHlvs(dates, showCurrent) {
    return nthNextPeriods(dates, showCurrent, dateToFormattedHlv);
}
export const nthNextHlvsByYearAndMonth = nthNextPeriodsByYearAndMonth(yearAndMonthToFormatted("H", 6));
/**
 * @deprecated use `yearAndMonthToFormattedSeason` instead
 */
function dateToFormattedSeason(date) {
    if (!date)
        throw new Error("`date` is undefined in dateToFormattedSeason function");
    const month = date.month() + 1;
    if (month < 4) {
        return `Win ${date.subtract(1, "year").format("YY")}`;
    }
    if (month < 10) {
        return `Sum ${date.format("YY")}`;
    }
    return `Win ${date.format("YY")}`;
}
function yearAndMonthToFormattedSeason(year, month) {
    if (month < 3) {
        return `Win ${TwoDigitYear.add(-1, year)}`;
    }
    if (month < 9) {
        return `Sum ${year}`;
    }
    return `Win ${year}`;
}
/**
 * @deprecated use `nthNextSeasonsByYearAndMonth` instead
 */
export function nthNextSeasons(dates, showCurrent) {
    return nthNextPeriods(dates, showCurrent, dateToFormattedSeason);
}
export const nthNextSeasonsByYearAndMonth = nthNextPeriodsByYearAndMonth(yearAndMonthToFormattedSeason);
function dateToFormattedCal(date) {
    if (!date)
        throw new Error("`date` is undefined in dateToFormattedCal function");
    return `Cal ${date.format("YY")}`;
}
function yearAndMonthToFormattedCal(year, _month) {
    return `Cal ${year}`;
}
/**
 * @deprecated use `nthNextCalsByYearAndMonth` instead
 */
export function nthNextCals(dates, showCurrent) {
    return nthNextPeriods(dates, showCurrent, dateToFormattedCal);
}
export const nthNextCalsByYearAndMonth = nthNextPeriodsByYearAndMonth(yearAndMonthToFormattedCal);
/**
 * @deprecated use `allPeriodsCountYearAndMonth` instead
 */
export function allPeriodsCount(p, date) {
    const monthsUserLimit = p.months ?? defaultMonthsRangeSettings;
    const monthListMonths = genMonthList(monthsUserLimit, date);
    const monthRows = nthNextMonths(monthListMonths);
    const qtrsInMonths = 3;
    const qtrsUserLimit = p.qtrs ? p.qtrs * qtrsInMonths : defaultMonthsRangeSettings;
    const monthListQtrs = genMonthList(qtrsUserLimit - (qtrsInMonths - 1), date);
    const qtrRows = nthNextQtrs(monthListQtrs, !!p.qtr_current);
    const hlvsInMonths = 6;
    const hlvsUserLimit = p.hlvs ? p.hlvs * hlvsInMonths : defaultMonthsRangeSettings;
    const monthListHlvs = genMonthList(hlvsUserLimit - (hlvsInMonths - 1), date);
    const hlvRows = nthNextHlvs(monthListHlvs, !!p.hlv_current);
    const seasonsInMonths = 6;
    const seasonsUserLimit = p.seasons ? p.seasons * 6 : defaultMonthsRangeSettings;
    const monthListSeasons = genMonthList(seasonsUserLimit - (seasonsInMonths - 1), date);
    const seasonRows = nthNextSeasons(monthListSeasons, !!p.season_current);
    const calsInMonths = 12;
    const calsUserLimit = p.cals ? p.cals * calsInMonths : defaultMonthsRangeSettings;
    const monthListCals = genMonthList(calsUserLimit - (calsInMonths - 1), date);
    const calRows = nthNextCals(monthListCals, !!p.cal_current);
    return {
        months: monthRows,
        qtrs: qtrRows,
        hlvs: hlvRows,
        seasons: seasonRows,
        cals: calRows,
    };
}
/**
 * this is 40 - 50 times faster than `allPeriodsCount`
 * because `genMonthList` is very slow, as are the formatting functions used
 */
export function allPeriodsCountYearAndMonth({ months, qtrs, qtr_current, hlvs, hlv_current, seasons, season_current, cals, cal_current }, date) {
    const monthsUserLimit = months ?? defaultMonthsRangeSettings;
    const monthRows = nthNextMonthsByYearAndMonth(genYearAndMonthList(monthsUserLimit, date));
    const qtrsInMonths = 3;
    const qtrsUserLimit = qtrs ? qtrs * qtrsInMonths : defaultMonthsRangeSettings;
    const qtrRows = nthNextQtrsByYearAndMonth(genYearAndMonthList(qtrsUserLimit - (qtrsInMonths - 1), date), qtr_current ?? false);
    const hlvsInMonths = 6;
    const hlvsUserLimit = hlvs ? hlvs * hlvsInMonths : defaultMonthsRangeSettings;
    const hlvRows = nthNextHlvsByYearAndMonth(genYearAndMonthList(hlvsUserLimit - (hlvsInMonths - 1), date), hlv_current ?? false);
    const seasonsInMonths = 6;
    const seasonsUserLimit = seasons ? seasons * 6 : defaultMonthsRangeSettings;
    const seasonRows = nthNextSeasonsByYearAndMonth(genYearAndMonthList(seasonsUserLimit - (seasonsInMonths - 1), date), season_current ?? false);
    const calsInMonths = 12;
    const calsUserLimit = cals ? cals * calsInMonths : defaultMonthsRangeSettings;
    const calRows = nthNextCalsByYearAndMonth(genYearAndMonthList(calsUserLimit - (calsInMonths - 1), date), cal_current ?? false);
    return {
        months: monthRows,
        qtrs: qtrRows,
        hlvs: hlvRows,
        seasons: seasonRows,
        cals: calRows,
    };
}
/**
 * Converts a month code string potentially in the format YYYY-MM-DD to a month code.
 * Returns `undefined` if not a valid ISOCalendarDateString
 *
 * @param monthStr - The month code string potentially in the format YYYY-MM-DD.
 * @returns - The month code.
 * @throws - If the month code string is undefined.
 */
export function monthStringToCode(monthStr) {
    return ISODateString.isISODateString(monthStr) ? MonthCode.fromISODateString(monthStr) : undefined;
}
export const isRowEqual = (row1, row2) => {
    return (row1.rowType === row2.rowType &&
        row1.period === row2.period &&
        row1.code === row2.code &&
        row1.ref === row2.ref &&
        row1.current === row2.current &&
        row1.firstOfType === row2.firstOfType);
};
export const rowTypeIsAdhoc = (rowType) => rowType === "adhoc";
const dateParseCache = {};
function monthToNumber(month) {
    if (!month || !ShortMonth.is(month)) {
        throw new Error("month is undefined in monthToNumber function");
    }
    return ShortMonth.monthNumberStringFromShortMonth(month);
}
/**
 * The reason this isn't in ISODateString.ts is because it assumes:
 * 1. the year must be 20XX
 * 2. the day must be the 1st
 * These are feature-specific assumptions and not strictly relevant to ISODateString
 *
 * This is faster than `parseAndFormatDate` because it doesn't use dayjs
 */
export const dateFromShortMonthYY = (shortMonth) => {
    // Extract the year assuming it's in the 2000s
    const year = TwoDigitYear.fromShortMonthYY(shortMonth);
    const month = ShortMonth.monthNumberStringFromShortMonthYY(shortMonth);
    return `20${year}-${month}-01`;
};
// TODO: it'd be great if we knew what gridMonth looked like
export function parseAndFormatDate(gridMonth) {
    const res = memoize0(dateParseCache, gridMonth, () => {
        try {
            if (gridMonth.startsWith("20")) {
                return `${dayjs(gridMonth).utc(true).format("YYYY-MM")}-01`;
            }
            // TODO: this assumes gridMonth is a ShortMonthAndYear
            // if not, the dayjs formatter will produce "Invalid Date"
            // can we verify this? if so, replace some of these with ShortMonthAndYear-specific functions
            // TODO: this is equivalent to dateFromShortMonthYY, we should be able to swap it out
            const month = monthToNumber(gridMonth.substring(0, 3));
            const year = `20${gridMonth.substring(3)}`;
            const res = `${dayjs(`${year}-${month}-01`).utc(true).format("YYYY-MM")}-01`;
            return res;
        }
        catch (error) {
            throw new Error(`Error in parseAndFormatDate: ${gridMonth} ${error}`);
        }
    });
    return res;
}
// Ensure the string isn't empty or falsy
// should be the inverse of !month
export const isNonEmptyMonthString = (month) => Boolean(month);
export function getSafeOffset(month) {
    // First try to standardize the month format
    const standardizedMonth = standardizeMonthCode(month);
    // Try with the standardized month first
    if (standardizedMonth !== month) {
        const standardizedOffset = tryRowToOffset(standardizedMonth);
        if (standardizedOffset !== undefined) {
            return standardizedOffset;
        }
    }
    // Fall back to the original month code
    const offset = tryRowToOffset(month);
    if (offset === undefined) {
        // If we still don't have an offset, it must be an older row that is still in the cache so we ignore it
        return null;
    }
    return offset;
}
/**
 * WARNING: this function is unsafe and will throw if given an empty or falsy string
 * Given a month string, return the number of months in the future that month is.
 * Note that the webapp will restart every day,
 * so we can assume that the current date is always the current date.
 *
 * @param month - month in the format "MMMYY" or "YYYY-MM-DD" or monthCode i.e. F24.
 *
 * @returns {number} - The offset as the difference in months between the current date-time and the target date-time.
 */
export function getOffset(month) {
    if (isNonEmptyMonthString(month)) {
        return getSafeOffset(month);
    }
    // preserve original throwing behaviour
    throw new Error("month is undefined in getOffset function");
}
export const monthCodeToShortMonth = ShortMonth.fromMonthCodeYY;
export function monthCodeToShortMonthAndYear(monthCode) {
    // TODO: this isn't safe (and wasn't before), but we'll keep the external API the same for now
    return ShortMonthYY.fromMonthCodeYY(monthCode);
}
export function rowIdToLiveChartPeriod(rowId) {
    return MonthCode.is(rowId) ? monthCodeToShortMonthAndYear(rowId) : rowId;
}
/**
 * Standardizes different month formats to a consistent format.
 * Handles cases like 'F25', 'FEB25', etc.
 *
 * @param monthCode - The month code to standardize
 * @returns - The standardized month code in the format expected by the system
 */
export function standardizeMonthCode(monthCode) {
    // Standard 3-char month code format (e.g., F25)
    if (MonthCode.is(monthCode)) {
        return monthCode;
    }
    // 3-letter month abbreviation with year (e.g., FEB25)
    if (ShortMonthYY.isCiShortMonthYY(monthCode)) {
        return MonthCode.fromCiShortMonthYY(monthCode);
    }
    // If we can't standardize, return the original
    return monthCode;
}
const monthCodeToOffsetToCache = {};
/**
 * This is only used by the side-effecting monthCodeToOffset below,
 * but is exposed for testing because it's complicated
 * and used to handle arbitrary strings poorly.
 * It could just return undefined for the failure case,
 * but because it's memoised, let's keep the return type monomorphic
 */
export const standardizedMonthCodeToShortMonth = (val) => {
    // Handle standard 3-char month codes like 'F25'
    if (MonthCode.is(val)) {
        return ShortMonthYY.fromMonthCodeYY(val);
    }
    // Handle full month names like 'FEB25' (although these should already be standardized)
    if (ShortMonthYY.isCiShortMonthYY(val)) {
        return ShortMonthYY.fromCiShortMonthYY(val);
    }
    /*
     * it's unclear what to do here - the *original* code was this:
     * ```
     *   // Default case - use original logic
     * else {
     *   month = monthCodeToShortMonth(standardizedCode);
     *   year = standardizedCode.slice(-2);
     * }
     * return `${month}${year}`;
     * ```
     * where `monthCodeToShortMonth` looked like:
     * monthCodeInverseLookup[char.charAt(0)] || ""
     * where monthCodeInverseLookup is MonthCode => ShortMonth
     * i.e.
     * F: "JAN";
     * G: "FEB";
     * H: "MAR";
     *
     * which for "F" would be "JANF" - not a sensible result.
     * Returning JUST an empty string would cause getOffset to throw.
     * I think this was avoiding causing getOffset to throw just by accident!
     * So now, in `monthCodeToOffset` guard the result and use getSafeOffset instead
     */
    return "";
};
export function monthCodeToOffset(monthCode) {
    // First standardize the month code format
    const standardizedCode = standardizeMonthCode(monthCode);
    return memoize0(monthCodeToOffsetToCache, standardizedCode, () => {
        const r = standardizedMonthCodeToShortMonth(standardizedCode);
        return isNonEmptyMonthString(r) ? getSafeOffset(r) : null;
    });
}
function genTimespreadData(numMonths, date) {
    const monthList = genYearAndMonthList(numMonths ?? defaultMonthsRangeSettings, date);
    const nextMonths = nthNextMonthsByYearAndMonth(monthList);
    const nextQtrs = nthNextQtrsByYearAndMonth(monthList, true);
    const nextHlvs = nthNextHlvsByYearAndMonth(monthList, true);
    const nextCal = nthNextCalsByYearAndMonth(monthList, true);
    const nextSeasons = nthNextSeasonsByYearAndMonth(monthList, true);
    return {
        nextMonths,
        nextQtrs,
        nextHlvs,
        nextCal,
        nextSeasons,
    };
}
export const timespreadData = genTimespreadData(defaultMonthsRangeSettings, londonNow());
export function genMonthCodeWithOffset(startMonth, monthOffsetInt) {
    const monthOffsetSteps = MonthCode.monthIndexFromMonthCodeYY(startMonth) + monthOffsetInt;
    // Calculate the new month index considering the cycle through the months
    const offsetMonthCode = MonthCode.monthCodeFromNumber(monthOffsetSteps);
    // Calculate year change. Each step of 12 months equals one year.
    // This calculation correctly handles negative offsets.
    const yearsChange = Math.floor(monthOffsetSteps / 12);
    const offsetYears = TwoDigitYear.asTwoDigitNumberFromMonthCodeYY(startMonth) + yearsChange;
    // assume this is valid given the calculations above are correct
    // it will be incorrect if offsetYears is > 99 or < 0
    // in that scenario, this will currently type check despite being wrong.
    // It will hopefully never occur, but an incorrect clock could cause this.
    return `${offsetMonthCode}${offsetYears}`;
}
// this should be equivalent to the below function but has a narrower domain
export function safeFormatMonthColumnText(month) {
    return `${ShortMonth.fromShortMonthYY(month)} ${TwoDigitYear.fromShortMonthYY(month)}`;
}
export function formatMonthColumnText(month) {
    if (month) {
        // ensure there is a space before the year
        const formatted = month.length > 3 && month[month.length - 3] !== " " ? `${month.slice(0, -2)} ${month.slice(-2)}` : month;
        return formatted;
    }
    return "";
}
export const liveChartsPeriods = nthNextMonthsByYearAndMonth(genYearAndMonthList(35, londonNow())).map((m) => {
    const mth = m[1];
    const parsed = `${dateFromShortMonthYY(mth)}T00:00:00.000000Z`;
    return {
        label: mth,
        value: parsed,
    };
});
const defaultGivenEmpty = (x, fallback) => x === undefined || x.trim() === "" ? fallback : x;
/**
 * Format adhoc spread for row name
 */
export const formatAdhocSpreadsText = (from, to, isStrip, spreadType, middle, middle2) => {
    const type = spreadType || (isStrip ? "strip" : "spread");
    if (type === "strip") {
        return `${defaultGivenEmpty(from, "-")}:${defaultGivenEmpty(to, "-")}`;
    }
    if (type === "fly") {
        return `${defaultGivenEmpty(from, "-")}/${defaultGivenEmpty(middle, "-")}/${defaultGivenEmpty(to, "-")}`;
    }
    if (type === "condor") {
        return `${defaultGivenEmpty(from, "-")}/${defaultGivenEmpty(middle, "-")}/${defaultGivenEmpty(middle2, "-")}/${defaultGivenEmpty(to, "-")}`;
    }
    return `${defaultGivenEmpty(from, "-")}/${defaultGivenEmpty(to, "-")}`;
};
export function memoize0(obj, tag, func) {
    if (!Object.hasOwn(obj, tag)) {
        Object.defineProperty(obj, tag, {
            enumerable: false,
            writable: false,
            value: func(),
        });
    }
    return obj[tag];
}
/** Returns a number for month row and string code for aggregated rows */
export const relativeRowToRowRef = (rowId) => {
    const rowType = relativeRowToRowType(rowId);
    const id = relativeRowToRowId[rowId];
    if (!id)
        return undefined;
    if (rowType === "mth")
        return monthCodeToOffset(id);
    return miscPeriodToCode(id);
};
//# sourceMappingURL=periods.js.map